import { Button, Card, Grid, Snackbar, Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useLayoutEffect, useState } from 'react';

import Loader from '../../../components/Loader';
import PatientInfo from '../../../components/PatientInfo';
import { IRequest, RequestStatus, RequestType, ServiceType, SurgeryStatus } from './types';

import ShowPatientRecords from '../../../components/investigation/show/single/show_patient_records';
import { TYPE_FILL_IMG_SURVEY, TYPE_FILL_LAB_SURVEY, TYPE_FILL_SHOE_SURVEY, TYPE_FILL_SURGERY_SURVEY, TYPE_REQUEST_LAB, TYPE_REQUEST_SHOE, TYPE_REQUEST_SURGERY } from '../../../constants';
import { IResearcher, ISurvey, SnackbarTypeSeverity } from '../../../constants/types';
import FillDataCollection from '../FillDataCollection';
import { usePatients, useRequest, useSnackBarState, useUpdateEffect } from '../../../hooks';
import { Alert } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import RequestInfo from './RequestInfo';
import { PERMISSION } from '../../../components/investigation/share/user_roles';
import { RequestStatusToChip } from './RequestTable';
import Modal from '../../../components/general/modal';
import { HOSPITAL_BILLING_PATIENT } from '../../../routes/urls';
import CancelRequestAction from './CancelRequestAction';
import RemoveServiceAction from './RemoveServiceAction';
import AddServiceAction from './AddServiceAction';
import ManualCompleteAction from './ManualCompleteAction';
import MaterialDeliveredAction from './MaterialDeliveredAction';
import RequestManagementPanel from './RequestManagementPanel';
import { canCancelRequest, canManuallyCompleteRequest, canMarkMaterialDelivered, canModifyRequestServices } from './requestModification';
import { isSlimSubmission } from '../../../utils/slimSubmissions';



interface RequestSingleProps { 
    idRequest:number,
    uuidInvestigation:string,
    permissions:string[],
    surveys:ISurvey[],
    country:string,
    researcher:IResearcher, 
    requestServiceType:ServiceType,
    requestSentCallBack:(typeRequest:number)=>void,
}

interface RequestActionsProps {
    request: IRequest;
    requestServiceType: ServiceType;
    updateRequestStatus: (idRequest: number, status: SurgeryStatus) => Promise<void>;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
}

// SHER-909: `onEdit` is gone. It was declared, passed from RequestSingle and never read —
// dead on arrival — and carrying it through the regroup would have preserved a prop whose
// only effect was to suggest this panel had an edit affordance it never had.
/**
 * Whether the surgical status row has anything to offer.
 *
 * SHER-909: RequestSingle has to know this too — it decides whether to hand the panel a
 * status group at all, and an empty group would leave the panel showing a titled section
 * with nothing under it. Exported rather than re-derived there, because two copies of this
 * condition would drift and the second copy would be the one nobody tests.
 *
 * The three clauses, unchanged from SHER-236: only surgical requests have a server-driven
 * state machine; a completed one has nowhere left to go; and an empty transition list with
 * no outstanding balance is a real answer, not a missing one — a cancelled request, or one
 * whose next move is a payment nobody owes.
 */
export function hasStatusActions(request: IRequest): boolean {
    if (request.type !== RequestType.SURGERY || request.status === RequestStatus.COMPLETED) {
        return false;
    }
    return (request.nextStatuses ?? []).length > 0 || (request.owesPayment ?? false);
}

export const RequestActions: React.FC<RequestActionsProps> = ({ 
    request, 
    requestServiceType, 
    updateRequestStatus,
    setShowSnackbar 
}) => {
    const [modal, setModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [nextStatus, setNextStatus] = useState<SurgeryStatus | null>(null);

    const handleStatusUpdate = async () => {
        setLoading(true);
        try {
            await updateRequestStatus(request.id, nextStatus as SurgeryStatus);
            setShowSnackbar({
                show: true,
                message: "pages.hospital.services.request.status_updated",
                severity: SnackbarTypeSeverity.SUCCESS
            });
        } catch (error) {
            const errorCode = error.response.data.errorCode;
            let errorString = "general.error";
            if(errorCode === 1){
                errorString = "pages.hospital.services.request.error_code_1";
            }
            setShowSnackbar({
                show: true,
                message: errorString,
                severity: SnackbarTypeSeverity.ERROR
            });
        }
        setLoading(false);
        setModal(false);
    };

    // SHER-236: gate on the request's own type, which is the condition the API uses to
    // decide whether to attach nextStatuses/owesPayment. requestServiceType is a route-level
    // prop describing the SERVICE, and the surgical list is queried by service type, so the
    // two can disagree — a request carrying a surgery-typed service but not itself a SURGERY
    // request would reach here with the fields legitimately absent. Matching the server's
    // condition means that inside this guard their absence is a contract fault, not a state.
    if (!hasStatusActions(request)) {
        return null;
    }

    // SHER-236: the API decides which transitions are on offer. They depend on the
    // linked bill and on whether the pre-anaesthesia visit already happened, and this
    // component holds neither, so a local copy of the state machine could only guess.
    const nextPossibleStatus = request.nextStatuses ?? [];
    const nextUrl = HOSPITAL_BILLING_PATIENT.replace(":uuidPatient", request.requestsServiceInvestigation[0].patientInvestigation.uuid);
    // Owing money is not a transition, so a patient who still owes may get no status
    // buttons at all. The way to billing has to stand on its own or the desk loses it —
    // and the server says whether money is owed, because status does not: in the VPA-first
    // pathway the patient owes the balance while sitting in "VPA en attente".
    const owesPayment = request.owesPayment ?? false;

    return (
        <>
        <Modal key="modal"
                open={modal}
                closeModal={() => setModal(false)}
                confirmAction={handleStatusUpdate}
                disabled={loading}
                title={<Translate id="pages.hospital.services.request.status_update.title" />}>
                    <Typography variant="h6" gutterBottom>
                        <Translate id="pages.hospital.services.request.status_update.modal" />
                        <RequestStatusToChip status={nextStatus as SurgeryStatus} />
                    </Typography>
                    
            </Modal>
        {/* SHER-909: no Card and no Grid item of its own any more. This used to be a second
            card sitting beside the modify/cancel one, which is what made the page read as
            two unrelated panels for one request. The shared panel owns the card now; this
            is one row inside it. */}
        <Typography variant="subtitle1" style={{fontWeight:"bold"}} gutterBottom>
            <Translate id="pages.hospital.services.request.status_update.description" />
        </Typography>
        <Grid container spacing={2}> 
                <>
                    <Grid item>
                        {
                            nextPossibleStatus.map((status) => <Button onClick={() => {setModal(true); setNextStatus(status)}}><RequestStatusToChip status={status} /></Button>)
                        }
                        {
                            owesPayment && <Button onClick={() => window.open(nextUrl, '_blank')}><Translate id="pages.hospital.services.request.status_update.go_to_billing" /></Button>
                        }
                    </Grid>        
                </>
        </Grid>
        </>
    );
};

const RequestSingle: React.FC<RequestSingleProps> = ({ idRequest, researcher, uuidInvestigation, country,
                                                        requestServiceType, permissions, surveys, requestSentCallBack }) => {
    const {request, loadingRequest} = useRequest(idRequest);
    const { patients, loadingPatients } = usePatients(uuidInvestigation);
    const patientsSubmissions = useSelector((state:any) => state.patientsSubmissions)
    const [loading, setLoading] = useState(false);
    const [submissionData, setSubmissionData] = useState<{submission:any} | null>(null);
    const [editData, setEditData] = useState(false);
    const [snackbar, setShowSnackbar] = useSnackBarState(); 

    useEffect(() => {
        if(request && patientsSubmissions.loading === false){
            setEditData(false);
            if(patientsSubmissions.error){
                let errorMessage = "general.error";
                
                // Check for specific error code
                if(patientsSubmissions.error === 'CAN_NOT_COMPLETE_REQUEST_NOT_ACCEPTED'){
                    errorMessage = "pages.hospital.services.request.error_request_not_accepted";
                }
                
                setShowSnackbar({show:true, message: errorMessage, severity:SnackbarTypeSeverity.ERROR});
            }
            else{
                setShowSnackbar({show:true, severity:SnackbarTypeSeverity.SUCCESS, message:"pages.hospital.services.request.success"});
            }
        }
        
    }, [patientsSubmissions])

    useEffect(() => {
        async function fetchSubmission(){
            setLoading(true);
        
            const response = await axios(`${import.meta.env.VITE_APP_API_URL}/researcher/investigation/${uuidInvestigation}/submission/${request?.submissionPatient.id}?findRequests=true`, { headers: {"Authorization" : localStorage.getItem("jwt")} })
                    .catch(err => {console.log('Catch', err); return err;});
            if(response.status === 200){
                setSubmissionData(response.data);
            }
            setLoading(false);
        }
        if(request?.submissionPatient){
            fetchSubmission();
        }
        
    }, [request])

    const updateRequestStatus = async (idRequest: number, status: SurgeryStatus) => {
        try {
            const response = await axios.put(
               
                `${import.meta.env.VITE_APP_API_URL}/hospital/${uuidInvestigation}/surgery/request/${idRequest}/status`,
                { status },
                { headers: { "Authorization": localStorage.getItem("jwt") } }
            );
            
            if (response.status === 200) {
                // Update the local request data
                if (request) {
                    const updatedRequest = { ...request, status };
                    // You'll need to add a way to update the request state here
                    // This could be through a setter function or Redux action
                }
            }
        } catch (error) {
            console.error('Error updating request status:', error);

            throw error;
        }
    };

    function renderCore(){
        const survey = surveys.find((survey) => {
            if(requestServiceType === ServiceType.LABORATORY){
                return survey.type === TYPE_FILL_LAB_SURVEY;
            }
            else if(requestServiceType === ServiceType.SHOE){
                return survey.type === TYPE_FILL_SHOE_SURVEY; 
            }
            else if(requestServiceType === ServiceType.SURGERY){
                return survey.type === TYPE_FILL_SURGERY_SURVEY;
            }
            else{
                return survey.type === TYPE_FILL_IMG_SURVEY;
            }
            
        })
        if(!survey){
            return <Typography variant="h4">The survey could not be found</Typography>
        }
        if(!permissions.includes(PERMISSION.UPDATE_LAB_REQUESTS) && !submissionData){
            return <Typography variant='body2'><Translate id="hospital.request.request_not_ready" /></Typography>;
        }
        else if(!editData && request?.submissionPatient && submissionData){ 
            const newSubmission = patientsSubmissions.data && patientsSubmissions.data[request?.requestsServiceInvestigation[0].patientInvestigation.uuid] && patientsSubmissions.data[request?.requestsServiceInvestigation[0].patientInvestigation.uuid][survey.uuid] ?  patientsSubmissions.data[request?.requestsServiceInvestigation[0].patientInvestigation.uuid][survey.uuid].submissions.find((submission:any) => submission.id === request?.submissionPatient.id) : null;
            // SHER-483 review: the clinical-history list now stores SLIM rows (no surveyRecords). If the
            // store override is a slim row, prefer the freshly-fetched full submission so the Request
            // detail doesn't render empty records.
            const submissionDataLocal = (newSubmission && !isSlimSubmission(newSubmission as any)) ? newSubmission : submissionData.submission;
            // SHER-680: the section printer icon needs the patient (PDF header) and the
            // investigation (author signature); without the patient the print modal never opened.
            const patient = patients?.find((p) => p.uuid === request.requestsServiceInvestigation[0].patientInvestigation.uuid);
            return (
                <ShowPatientRecords permissions={permissions} survey={survey} forceEdit={request.status !== RequestStatus.COMPLETED}
                    mode="elements" callBackEditSubmission={() => setEditData(true)} idSubmission={request?.submissionPatient.id}
                    submissions={[submissionDataLocal]} surveys={surveys}
                    patient={patient} uuidInvestigation={uuidInvestigation} />
            )
        }
        else{
            return (
                <FillDataCollection key={survey?.uuid} initData = {editData && submissionData ? submissionData.submission : null} 
                    dataCollection={survey} hideCollectionName={survey.sections.length === 1 ? true : false} requestServiceId={idRequest} 
                    idSubmission={submissionData ? submissionData.submission.id : null}
                    country={country} researcher={researcher} alterSubmitButton={requestServiceType !== ServiceType.SURGERY ? "pages.hospital.services.save_and_complete" : null}
                    uuidPatient={request?.requestsServiceInvestigation[0].patientInvestigation.uuid} 
                    uuidInvestigation={uuidInvestigation}
                    callBackDataCollection={() => console.log("Data saved")} />
            )
        }
    }

    function handleCloseSnackBar(){
        setShowSnackbar({show:false});
        if(snackbar.severity === SnackbarTypeSeverity.SUCCESS && request !== null){
            requestSentCallBack(request?.requestsServiceInvestigation?.[0]?.serviceInvestigation?.service?.type);
        }
    }

    if(loading || loadingRequest || (patientsSubmissions.loading) || loadingPatients){
        return <Loader />
    }
    else if(!request){
        return <Typography variant="h4">Request not found</Typography>
    }
    return (
        <>
            <Snackbar
                anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
                }}
                open={snackbar.show}
                autoHideDuration={4000}
                onClose={handleCloseSnackBar}
                >
                <Alert severity={snackbar.severity}>
                        <Translate id={snackbar.message} />                            
                    </Alert>
            </Snackbar>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card style={{padding:"1rem"}}>
                        <PatientInfo uuidPatient={request.requestsServiceInvestigation[0].patientInvestigation.uuid}/>
                    </Card>
                    
                </Grid>
                <Grid item xs={12}>
                    <Card style={{padding:"1rem"}}>
                        <RequestInfo request={request} />
                    </Card>
                </Grid>
                {
                    requestServiceType !== ServiceType.SURGERY && request.typeSpecificData&& (
                        <Grid item xs={12}>
                            <Card style={{padding:"1rem"}}>
                                <Typography variant="h6" style={{fontWeight:"bold"}} gutterBottom>
                                    <Translate id="pages.hospital.services.request.clinical_notes" />
                                </Typography>
                                <span dangerouslySetInnerHTML={{__html: request.typeSpecificData?.notes || ''}} />
                            </Card>
                        </Grid>
                    )
                }
                
                {/* SHER-909: one section for everything that can be done to this request.
                    The status panel used to be a Card of its own beside this one, and each
                    control brought its own bold heading; the panel carries the single
                    heading now and pushes cancellation to the bottom behind a rule. Each
                    child still decides for itself whether the user may see it, so the panel
                    is composed rather than gated on a fifth copy of the permission rules. */}
                <RequestManagementPanel
                    services={!canModifyRequestServices(request, permissions) ? null :
                        <RemoveServiceAction
                            request={request}
                            uuidInvestigation={uuidInvestigation}
                            permissions={permissions}
                            requestServiceType={requestServiceType}
                            onChanged={() => requestSentCallBack(request?.requestsServiceInvestigation?.[0]?.serviceInvestigation?.service?.type)}
                            setShowSnackbar={setShowSnackbar}
                        />
                    }
                    addService={!canModifyRequestServices(request, permissions) ? null :
                        <AddServiceAction
                            request={request}
                            uuidInvestigation={uuidInvestigation}
                            requestServiceType={requestServiceType}
                            permissions={permissions}
                            onChanged={() => requestSentCallBack(request?.requestsServiceInvestigation?.[0]?.serviceInvestigation?.service?.type)}
                            setShowSnackbar={setShowSnackbar}
                        />
                    }
                    status={!(hasStatusActions(request) || canManuallyCompleteRequest(request, requestServiceType, permissions) || canMarkMaterialDelivered(request, requestServiceType, permissions)) ? null :
                        <>
                            <RequestActions
                                requestServiceType={requestServiceType}
                                request={request}
                                updateRequestStatus={updateRequestStatus}
                                setShowSnackbar={setShowSnackbar}
                            />
                            <ManualCompleteAction
                                request={request}
                                requestServiceType={requestServiceType}
                                uuidInvestigation={uuidInvestigation}
                                permissions={permissions}
                                onCompleted={() => requestSentCallBack(request?.requestsServiceInvestigation?.[0]?.serviceInvestigation?.service?.type)}
                                setShowSnackbar={setShowSnackbar}
                            />
                            <MaterialDeliveredAction
                                request={request}
                                requestServiceType={requestServiceType}
                                uuidInvestigation={uuidInvestigation}
                                permissions={permissions}
                                onDelivered={() => requestSentCallBack(request?.requestsServiceInvestigation?.[0]?.serviceInvestigation?.service?.type)}
                                setShowSnackbar={setShowSnackbar}
                            />
                        </>
                    }
                    cancel={!canCancelRequest(request, permissions) ? null :
                        <CancelRequestAction
                            request={request}
                            uuidInvestigation={uuidInvestigation}
                            permissions={permissions}
                            onCancelled={() => requestSentCallBack(request?.requestsServiceInvestigation?.[0]?.serviceInvestigation?.service?.type)}
                            setShowSnackbar={setShowSnackbar}
                        />
                    }
                />
            </Grid>
            {
                renderCore()
            }
        </>
    );
};

export default RequestSingle;
